import "./address-book-header.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { MultiSelectChangeEvent } from "@tql/ui-components-react/kendo/dropdowns";
import { InputPrefix } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { MultiSelect } from "@tql/ui-components-react/tql/dropdowns";
import {
	arrowLeft,
	filterFunnel_01,
} from "@tql/ui-components-react/untitled/icons";
import type { Dispatch, SetStateAction } from "react";

import { ColumnChooser } from "~/shared/components/column-chooser/column-chooser.tsx";
import { CustomerSearch } from "~/shared/components/customer-search/customer-search.tsx";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import type { Customer } from "~/shared/types/quote-details-types";

import {
	getHistoryDate,
	useAddressBookHistory,
} from "../../hooks/use-address-book-history.ts";

const filters = ["Favorites", "User Defaults", "Verified", "Not Legacy"];

const formatDate = (date: Date) => {
	const month = (date.getMonth() + 1).toString().padStart(2, "0");
	const day = date.getDate().toString().padStart(2, "0");
	const year = date.getFullYear();

	return `${month}/${day}/${year}`;
};

type AddressBookHeaderProps = {
	addressId: number | undefined;
	selectedCustomer: Customer | undefined;
	setSelectedCustomer: (customer: Customer | null) => void;
	selectedAddressBookItem: number | undefined;
	showEditActions?: boolean;
	setSelectedAddressBookItem: (id: number | undefined) => void;
	selectedFilters: string[];
	setSelectedFilters: Dispatch<SetStateAction<string[]>>;
	onSave?: () => void;
	onCancel?: () => void;
	isLoading?: boolean;
	columns?: BoardColumn[];
	onColumnToggle?: (column: BoardColumn) => void;
	onResetColumns?: () => void;
	isLegacy?: boolean;
};

export const AddressBookHeader = ({
	addressId,
	selectedCustomer,
	setSelectedCustomer,
	selectedAddressBookItem,
	showEditActions,
	setSelectedAddressBookItem,
	selectedFilters,
	setSelectedFilters,
	onSave,
	onCancel,
	isLoading,
	columns,
	onColumnToggle,
	onResetColumns,
	isLegacy = false,
}: AddressBookHeaderProps) => {
	const {
		isReady: traxLtlLegacyFlipIsReady,
		isEnabled: traxLtlLegacyFlipIsEnabled,
	} = useFeatureFlagGate("traxLtlLegacyFlip");

	const { historyData } = useAddressBookHistory(addressId);

	const isMobile = useIsMobile();

	const shouldDisableLegacySave =
		isLegacy && traxLtlLegacyFlipIsReady && !traxLtlLegacyFlipIsEnabled;

	const shouldShowEditActions =
		showEditActions ?? Boolean(selectedAddressBookItem);

	const backToListButton = selectedAddressBookItem ? (
		<Button
			aria-label={"Back to address list"}
			data-testid={"address-book-back-to-list-button"}
			onClick={() => {
				setSelectedAddressBookItem(undefined);
			}}
			svgIcon={arrowLeft}
			variant={"tertiary"}
		/>
	) : null;

	const customerSearch = (
		<CustomerSearch
			disabled={false}
			onCustomerChange={setSelectedCustomer}
			selectedCustomer={selectedCustomer}
			wrapperStyle={{ width: "300px" }}
		/>
	);

	const historyInfo =
		addressId && historyData && historyData.length > 0 ? (
			<div className={"address-book-created-updated-by"}>
				<div>
					Created by{" "}
					<span className={"tql-link"}>
						{historyData.at(-1)?.createdByInternalEmail ||
							historyData.at(-1)?.createdByInternalName ||
							"Unknown"}
					</span>{" "}
					on {formatDate(getHistoryDate(historyData.at(-1)?.validFrom ?? ""))}
					&nbsp;-&nbsp;
				</div>
				<div>
					Last Updated by
					<span className={"tql-link"}>
						&nbsp;
						{historyData[0]?.createdByInternalEmail ||
							historyData[0]?.createdByInternalName ||
							"Unknown"}
					</span>{" "}
					on {formatDate(getHistoryDate(historyData[0]?.validFrom ?? ""))}
				</div>
			</div>
		) : null;

	const filterMultiSelect = addressId ? null : (
		<div className={"address-book-filter"}>
			<MultiSelect
				data={filters}
				fillMode={"outline"}
				onChange={(e: MultiSelectChangeEvent) =>
					setSelectedFilters(e.value as string[])
				}
				placeholder={"Favorites"}
				prefix={
					<InputPrefix orientation={"horizontal"}>
						<SvgIcon icon={filterFunnel_01} />
					</InputPrefix>
				}
				value={selectedFilters}
			/>
		</div>
	);

	const editActions = shouldShowEditActions ? (
		<div className={"address-book-header-buttons"}>
			<Button
				disabled={Boolean(isLoading) || shouldDisableLegacySave}
				onClick={onSave}
				variant={"primary"}
			>
				Save
			</Button>
			<Button disabled={isLoading} onClick={onCancel}>
				Cancel
			</Button>
		</div>
	) : null;

	if (isMobile) {
		return (
			<div className={"mobile-address-header"}>
				{backToListButton}
				<div className={"mobile-customer-search"}>{customerSearch}</div>
				{historyInfo}
				{filterMultiSelect}
				{editActions}
			</div>
		);
	}

	return (
		<div className={"address-book-header"}>
			{backToListButton}
			{customerSearch}
			{historyInfo}
			{!addressId && (
				<>
					{filterMultiSelect}
					<ColumnChooser
						secondary
						className={"address-book-column-chooser"}
						columns={columns ?? []}
						onColumnToggle={onColumnToggle ?? (() => {})}
						onResetColumns={onResetColumns}
					/>
				</>
			)}
			{editActions}
		</div>
	);
};
